import * as React from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { ConvertToIngestionRuleCallout } from "@/shared/components/ingestionRules";
import { ActionInvocationEditor } from "@/shared/components/invocationSpec/ActionInvocationEditor";
import { type TriggeringSubject } from "@/shared/components/invocationSpec/inputSourceCopy";
import { isIngestionAction } from "@/shared/domain/actions";
import {
  firesOnFileArrival,
  rulePrefillFromTrigger,
  type IngestionRulePrefill,
} from "@/shared/domain/ingestionRules";
import {
  TriggerSourceType,
  TriggerTargetType,
  variableRoots,
} from "@/shared/domain/triggers";

import { previewTargetSpec } from "../../buildTriggerRequest";
import { suppliedInputFor } from "../../formScope";
import { StoredTriggerContext } from "../../storedTriggerContext";
import { type TriggerFormValues } from "../../triggerFormSchema";
import { useFormTemplateCatalog } from "../../useFormTemplateCatalog";

import { InputDataHeader } from "./InputDataHeader";

export interface InvokeActionTargetCardProps {
  index: number;
}

/** Which firing the dataset row is worded for. */
function subjectOfFiring(
  supplied: ReturnType<typeof suppliedInputFor>,
): TriggeringSubject {
  return supplied === "changed-file" ? "file" : "dataset";
}

/** The action to invoke, and everything about how to run it. */
export function InvokeActionTargetCard({ index }: InvokeActionTargetCardProps) {
  const { control, getValues } = useFormContext<TriggerFormValues>();
  const storedTrigger = React.useContext(StoredTriggerContext);
  const firesOn = useWatch<TriggerFormValues, "firesOn">({
    control,
    name: "firesOn",
  });
  const catalog = useFormTemplateCatalog();

  const supplied = suppliedInputFor(firesOn);
  // Whether the firing puts a dataset in the namespace, which is what the
  // placeholder resolves against: an unresolved one fails the dispatch, so
  // this is read off the catalog rather than assumed from the entity.
  const carriesADataset =
    firesOn.type === TriggerSourceType.Events &&
    variableRoots(firesOn.events).includes("dataset");
  // A file firing's subject is the file, so the row names the dataset as that
  // file's rather than as one the trigger fired on.
  const triggeringSubject: TriggeringSubject | undefined = carriesADataset
    ? subjectOfFiring(supplied)
    : undefined;

  const ingestsUploads =
    firesOn.type === TriggerSourceType.Events &&
    firesOnFileArrival({
      type: TriggerSourceType.Events,
      events: firesOn.events,
      once_per: firesOn.oncePer,
    });
  // Read at click time, so the rule gets the form as it stands.
  const prefill = (): IngestionRulePrefill => {
    const values = getValues();
    const spec = previewTargetSpec(values, index);
    const invocation =
      spec?.type === TriggerTargetType.InvokeAction
        ? rulePrefillFromTrigger(
            {
              name: values.name,
              ...(storedTrigger !== null
                ? { trigger_id: storedTrigger.triggerId }
                : {}),
            },
            spec,
          )
        : { includePatterns: [], excludePatterns: [] };
    const name = values.name.trim();
    return { ...invocation, name: name === "" ? undefined : name };
  };

  return (
    <ActionInvocationEditor
      name={`targets.${index}.spec`}
      requiredParametersName={`targets.${index}.requiredParameters`}
      actionHint="The action to run when this trigger fires."
      templateCatalog={catalog}
      inputDataHeader={
        supplied === null ? undefined : (
          <InputDataHeader index={index} supplied={supplied} />
        )
      }
      hasSourceDataset={carriesADataset}
      triggeringSubject={triggeringSubject}
      renderActionNotice={(action) =>
        ingestsUploads && action !== null && isIngestionAction(action) ? (
          <ConvertToIngestionRuleCallout prefill={prefill} />
        ) : null
      }
      data-testid={`trigger-target-${index}-action`}
    />
  );
}
